import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CuitBlurNormalizeDirective, cuitValidator } from '@webapp/core';
import { IZone } from '@webapp/core/interfaces/zones.interface';
import { ZonesService } from '@webapp/core/services/zones.service';
import {
	ButtonComponent,
	ButtonSwitchComponent,
	IActivarButtonSwitch,
	InputErrorComponent,
} from '@webapp/ui';
import { NgxMaskDirective } from 'ngx-mask';
import { ICreateAdminPayload, USER_TYPES } from '../../../interfaces/users.interface';
import { UsersService } from '../../../services/users.service';

@Component({
	selector: 'app-create-admin-form',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		ButtonComponent,
		ButtonSwitchComponent,
		InputErrorComponent,
		NgxMaskDirective,
		CuitBlurNormalizeDirective,
	],
	templateUrl: './create-admin-form.component.html',
	styleUrl: './create-admin-form.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateAdminFormComponent {
	private readonly formBuilder = inject(FormBuilder);
	private readonly zonesService = inject(ZonesService);
	private readonly usersService = inject(UsersService);

	// Inputs
	loading = input<boolean>(false);

	// Outputs
	formSubmit = output<ICreateAdminPayload>();

	// Signals
	readonly submitted = signal(false);
	readonly zones = signal<IZone[]>([]);
	readonly selectedZone = signal<string>('');
	readonly checkingPostalCode = signal(false);
	readonly clientType = signal<'person' | 'company'>('person');

	readonly typeButtons: IActivarButtonSwitch[] = [
		{ id: 1, name: 'Persona', type: 'person' },
		{ id: 2, name: 'Empresa', type: 'company' },
	];

	readonly form: FormGroup = this.formBuilder.group({
		firstName: ['', [Validators.required]],
		lastName: ['', [Validators.required]],
		companyName: ['', []],
		identification: ['', [Validators.required]],
		email: [
			'',
			[Validators.required, Validators.email],
			[this.usersService.checkEmailExists()],
		],
		address: ['', [Validators.required]],
		postalCode: ['', [Validators.required]],
		zoneId: ['', [Validators.required]],
	});

	protected get isPersonType(): boolean {
		return this.clientType() === USER_TYPES.PERSON;
	}

	protected get isCompanyType(): boolean {
		return this.clientType() === USER_TYPES.COMPANY;
	}

	protected get identificationLabel(): string {
		return this.isPersonType ? 'DNI' : 'CUIT';
	}

	protected get identificationMask(): string {
		return this.isPersonType ? '00.000.000' : '00-00000000-0';
	}

	protected setClientType(button: IActivarButtonSwitch): void {
		if (button.type !== USER_TYPES.PERSON && button.type !== USER_TYPES.COMPANY) return;

		this.clientType.set(button.type);

		const identificationControl = this.form.get('identification');
		identificationControl?.setValidators(
			button.type === USER_TYPES.COMPANY
				? [Validators.required, cuitValidator]
				: [Validators.required]
		);
		identificationControl?.updateValueAndValidity();

		const firstNameControl = this.form.get('firstName');
		const lastNameControl = this.form.get('lastName');
		const companyNameControl = this.form.get('companyName');

		if (button.type === USER_TYPES.COMPANY) {
			firstNameControl?.clearValidators();
			firstNameControl?.reset('');
			lastNameControl?.clearValidators();
			lastNameControl?.reset('');
			companyNameControl?.setValidators([Validators.required]);
		} else {
			companyNameControl?.clearValidators();
			companyNameControl?.reset('');
			firstNameControl?.setValidators([Validators.required]);
			lastNameControl?.setValidators([Validators.required]);
		}

		firstNameControl?.updateValueAndValidity();
		lastNameControl?.updateValueAndValidity();
		companyNameControl?.updateValueAndValidity();
	}

	protected checkPostalCode(): void {
		const postalCode = this.form.get('postalCode')?.value;
		if (!postalCode) return;

		this.checkingPostalCode.set(true);
		this.zonesService.getZones(postalCode).subscribe({
			next: zones => {
				this.checkingPostalCode.set(false);
				if (zones.length === 1) {
					this.form.get('zoneId')?.setValue(zones[0].id);
					this.form.get('zoneId')?.markAsDirty();
					this.selectedZone.set(zones[0].id);
					this.zones.set([]);
				} else {
					this.zones.set(zones);
					this.selectedZone.set('');
					this.form.get('zoneId')?.setValue('');
				}
			},
			error: () => {
				this.checkingPostalCode.set(false);
				this.zones.set([]);
				this.selectedZone.set('');
			},
		});
	}

	protected onZoneSelect(event: Event): void {
		const target = event.target as HTMLSelectElement;
		this.selectedZone.set(target.value);
		this.form.get('zoneId')?.setValue(target.value);
		this.form.get('zoneId')?.markAsDirty();
	}

	protected onSubmit(): void {
		this.submitted.set(true);
		this.form.markAllAsTouched();

		if (this.zones().length > 1 && !this.selectedZone()) {
			return;
		}

		if (this.form.invalid) return;

		const raw = this.form.getRawValue();
		const payload: ICreateAdminPayload = {
			email: raw.email,
			zoneId: raw.zoneId || this.selectedZone(),
			address: raw.address,
			identification: Number(String(raw.identification).replace(/\D/g, '')),
			type: this.clientType(),
			...(this.isPersonType
				? { firstName: raw.firstName, lastName: raw.lastName }
				: { companyName: raw.companyName }),
		};

		this.formSubmit.emit(payload);
	}
}
